/**
 * +atmos: the sky dome (moved out of field.ts): a painted gradient (deep zenith, clear mid blue, a
 * pale horizon band), the sun (disc, halo, glow) or, at night, a moon and a few stars. Clouds are
 * separate billboards (clouds.ts). Every colour is a uniform the time of day drives in place.
 */
import * as THREE from 'three';
import { NOISE_GLSL, U } from '../../style';
import { ATMOS } from './uniforms';

export interface SkyUniforms {
  uTop: { value: THREE.Color };
  uMid: { value: THREE.Color };
  uHorizon: { value: THREE.Color };
  uNight: { value: number };
}

export function skyDome(sky: { top: number; mid: number; horizon: number }): { mesh: THREE.Mesh; u: SkyUniforms } {
  const geo = new THREE.SphereGeometry(330, 32, 16);
  const u: SkyUniforms = {
    uTop: { value: new THREE.Color(sky.top) },
    uMid: { value: new THREE.Color(sky.mid) },
    uHorizon: { value: new THREE.Color(sky.horizon) },
    uNight: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms: { ...u, uFog: U.uFogColor, uSun: ATMOS.uSunDir, uSunCol: ATMOS.uSunColor, uTime: U.uTime },
    vertexShader: `varying vec3 vDir; void main(){ vDir = normalize(position); vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position = p.xyww; }`,
    fragmentShader: `uniform vec3 uTop; uniform vec3 uMid; uniform vec3 uHorizon; uniform vec3 uFog; uniform vec3 uSun; uniform vec3 uSunCol; uniform float uTime; uniform float uNight; varying vec3 vDir;
      ${NOISE_GLSL}
      void main(){
        vec3 d = normalize(vDir); float h = d.y; float hp = max(h, 0.0); vec3 c;
        // Painted gradient: pale horizon band, clear mid blue, deep saturated zenith.
        c = mix(uHorizon, uMid, smoothstep(0.0, 0.2, hp));
        c = mix(c, uTop, smoothstep(0.14, 0.85, hp));
        vec3 sunDir = normalize(uSun);
        float sd = max(dot(d, sunDir), 0.0);
        float day = 1.0 - uNight;
        // Sun: wide warm glow, tighter halo and a soft-edged disc (a cool small moon at night).
        vec3 sunC = mix(vec3(1.0, 0.9, 0.72), vec3(0.75, 0.85, 1.0), uNight);
        float low = 1.0 - smoothstep(0.0, 0.5, sunDir.y);
        c += sunC * (pow(sd, 6.0) * (0.12 + 0.25 * low) + pow(sd, 90.0) * 0.45) * mix(1.0, 0.35, uNight);
        float disc = smoothstep(mix(0.99955, 0.99975, uNight), mix(0.9998, 0.99992, uNight), sd);
        c += mix(vec3(1.2, 1.1, 0.95) * 2.2, vec3(0.9, 0.95, 1.05) * 1.2, uNight) * disc;
        // Warm haze hugging the horizon (the sun side warmer, much warmer with a low sun).
        float sideSun = pow(max(dot(normalize(d.xz + vec2(1e-4)), normalize(sunDir.xz + vec2(1e-4))), 0.0), 3.0);
        c = mix(c, mix(uHorizon, sunC, (0.2 + 0.35 * low * day) * sideSun) * 1.04, (1.0 - smoothstep(0.0, 0.07 + 0.08 * low, hp)) * 0.55);
        // Stars (night only): sparse hashed cells, twinkling, fading toward the horizon.
        if (uNight > 0.01) {
          vec2 sp = d.xz / (h + 1.0) * 140.0;
          vec2 cell = floor(sp);
          float r = hash(cell);
          vec2 f = fract(sp) - 0.5 - (vec2(hash(cell + 3.1), hash(cell + 7.7)) - 0.5) * 0.6;
          float star = step(0.93, r) * smoothstep(0.12, 0.0, length(f)) * (0.6 + 0.4 * sin(uTime * (1.0 + r * 3.0) + r * 40.0));
          c += vec3(0.85, 0.9, 1.0) * star * uNight * smoothstep(0.05, 0.4, h) * 0.9;
        }
        c = mix(c, uFog, (1.0 - smoothstep(-0.02, 0.05, h)) * 0.85);
        gl_FragColor = vec4(c, 1.0);
      }`,
    side: THREE.BackSide,
    depthWrite: false,
  });
  const m = new THREE.Mesh(geo, mat);
  m.name = 'sky';
  m.renderOrder = -10;
  m.frustumCulled = false;
  return { mesh: m, u };
}
